import { getDefaultFieldValueMap, SubmissionDataDestination } from '@common/submission-data';
import { HeraldData } from '@common/types';
import { HeraldApplicationResponse } from '@common/types/herald/herald-types';
import { getIsHeraldSubmission } from '@common/utils';
import { forwardRef, useMemo } from 'react';
import { LoaderOverlay } from '@common-components';
import { useConditionallyRenderedModal } from 'hooks';
import { PartialSubmission } from 'types';
import { MainAreaDimensionsState } from 'broker/components/common/VerticalFormStepper/types';
import { SetNextStepsProps } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/EditSubmissionNew/useSetNextStep';
import { useSendPromptReport } from 'broker/utils/use-send-prompt-report';
import DynamicFormLayout from './DynamicFormLayout';
import {
  MinimalFormProceedDialog,
  MinimalFormProceedOpenDialogProps,
} from './DynamicFormLayout/components/MinimalFormProceedDialog';
import { DynamicFormImperativeHandle, DynamicFormState, FormStateBaseProp } from './DynamicFormLayout/types';
import { getVipQuestions } from './DynamicFormLayout/utils/vip-questions-config';
import { useGetFlowProvider } from './providers/flow/useGetFlowProvider';
import { getDynamicQuestions } from './providers/get-dynamic-questions';
import { useGetHeraldProvider } from './providers/herald/useGetHeraldProvider';
import { useDynamicFormPersistentState } from './useDynamicFormPersistentState';
import { useGetDynamicFormDisplayProps } from './useGetDynamicFormDisplayProps';
import { useGetExtractedData } from './useGetExtractedData';

interface DynamicFormProps {
  setIsDirty?: (isDirty: boolean) => void;
  onClose: () => void;
  applySuggestionsOnEmptyFields: boolean;
  mainAreaDimensionsState?: MainAreaDimensionsState;
  isAcknowledgmentFlow: boolean;
  setNextStep: (props?: SetNextStepsProps) => void;
  submission: PartialSubmission;
  firstVisit?: boolean;
}

export const DynamicForm = forwardRef<DynamicFormImperativeHandle, DynamicFormProps>(
  (
    {
      setIsDirty,
      onClose,
      mainAreaDimensionsState,
      isAcknowledgmentFlow,
      applySuggestionsOnEmptyFields,
      setNextStep,
      submission,
      firstVisit,
    },
    ref,
  ) => {
    const sendPromptReport = useSendPromptReport();
    const isHeraldSubmission = getIsHeraldSubmission(submission);
    const {
      openModal: openMinimalFormProceedDialog,
      closeModal: closeMinimalFormProceedDialog,
      modalState: minimalFormProceedDialogState,
    } = useConditionallyRenderedModal<MinimalFormProceedOpenDialogProps>({});

    const {
      key,
      setKey,
      extractionValidationFields,
      setExtractionValidationFields,
      scrollPositionOnMount,
      setScrollPositionOnMount,
      scrollPosition,
      setScrollPosition,
      activeSection,
      sectionRefs,
      scrollableDivRef,
      onSetActiveSection,
    } = useDynamicFormPersistentState();

    const vipQuestions = useMemo(
      () => (submission.coverageLines ? getVipQuestions(submission.coverageLines) : []),
      [submission.coverageLines],
    );

    const {
      isLoadingHeraldApplication,
      heraldDynamicQuestions,
      updateHeraldWithFormValues,
      heraldProducts,
      quoteExits,
    } = useGetHeraldProvider({
      isHeraldSubmission,
      submission,
      vipQuestions,
    });

    const { flowDynamicQuestions, updateSubmissionWithFormValues } = useGetFlowProvider({
      submission,
      isHeraldSubmission,
      heraldDynamicQuestions,
      isLoadingHeraldApplication,
    });

    const potentialDynamicQuestions = useMemo(
      () => getDynamicQuestions({ heraldDynamicQuestions, flowDynamicQuestions }),
      [flowDynamicQuestions, heraldDynamicQuestions],
    );

    const isLoading = isLoadingHeraldApplication;

    const { formMode, dynamicQuestions, setFormMode } = useGetDynamicFormDisplayProps({
      isHeraldSubmission,
      isLoading,
      isFirstVisit: firstVisit,
      potentialDynamicQuestions,
      vipQuestions,
    });

    const submissionExtractedData = useGetExtractedData(heraldDynamicQuestions, submission);

    const dynamicFormDefaultValues = useMemo(
      () =>
        getDefaultFieldValueMap(SubmissionDataDestination.SubmissionForm, {
          extractedData: submission.submissionExtractedData?.extractedData,
          selectedCoverageLines: submission.coverageLines,
        }),
      [submission],
    );

    const onFormSubmit = async (formState: DynamicFormState) => {
      let heraldResponse: HeraldApplicationResponse | undefined;
      let updatedHeraldData: Partial<HeraldData> | undefined;
      if (isHeraldSubmission) {
        heraldResponse = await updateHeraldWithFormValues(formState, formMode);
        if (heraldResponse) {
          updatedHeraldData = {
            status: heraldResponse.application.status,
          };
        }
      }
      await updateSubmissionWithFormValues(formState, dynamicQuestions, updatedHeraldData);
      setKey((prev) => prev + 1);
      if (submissionExtractedData) {
        // no need to await for this as it should not block the submission update
        sendPromptReport({
          formState,
          suggestedValueProps: submissionExtractedData,
          entityId: submission.id,
          submissionId: submission.id,
          getValue: (formValue) => {
            if (Array.isArray(formValue)) {
              return formValue.map((value) => value[FormStateBaseProp.Main]);
            }
            return formValue?.main;
          },
        });
      }
      return {
        heraldResponse,
      };
    };

    if (isLoading || !formMode) {
      return <LoaderOverlay hideOverlay />;
    }

    return (
      <>
        <DynamicFormLayout
          formMode={formMode}
          setFormMode={setFormMode}
          coverageLines={submission.coverageLines}
          dynamicQuestions={dynamicQuestions}
          setNextStep={setNextStep}
          submissionExtractedData={submissionExtractedData}
          isAcknowledgmentFlow={isAcknowledgmentFlow}
          key={key}
          onFormSubmit={onFormSubmit}
          setIsDirty={setIsDirty}
          sectionRefs={sectionRefs} // Pass refs to DynamicForm
          activeSection={activeSection}
          setActiveSection={onSetActiveSection} // Use onSetActiveSection for stepper clicks
          onClose={onClose}
          mainAreaDimensionsState={mainAreaDimensionsState}
          scrollPositionOnMount={scrollPositionOnMount}
          setScrollPositionOnMount={setScrollPositionOnMount}
          scrollPosition={scrollPosition}
          setScrollPosition={setScrollPosition}
          applySuggestionsOnEmptyFields={key === 0 && applySuggestionsOnEmptyFields}
          ref={ref}
          scrollableDivRef={scrollableDivRef}
          quoteExits={quoteExits}
          setExtractionValidationFields={setExtractionValidationFields}
          extractionValidationFields={extractionValidationFields}
          heraldProducts={heraldProducts}
          isHeraldSubmission={isHeraldSubmission}
          dynamicFormDefaultValues={dynamicFormDefaultValues}
          vipQuestions={vipQuestions}
          openMinimalFormProceedDialog={openMinimalFormProceedDialog}
        />
        {minimalFormProceedDialogState.shouldRender && (
          <MinimalFormProceedDialog
            onProceed={onClose}
            onClose={closeMinimalFormProceedDialog}
            isOpened={minimalFormProceedDialogState.isOpen}
            {...minimalFormProceedDialogState.modalProps!}
          />
        )}
      </>
    );
  },
);

export default DynamicForm;
